import React, { Fragment } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";
import { RootState } from "~/redux/rootReducer";
import { faceType } from "~/const/options";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  IconButton,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RestoreFromTrashIcon from "@material-ui/icons/RestoreFromTrash";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "~/components/Accordion";
import { OutlineSelect, OutlineTextField, RadioSelect } from "~/components";
import { Module } from "../hooks";

type Props = {
  open: boolean;
  onClose: () => void;
  module: Module;
};

export const PatternDialog: React.VFC<Props> = (props) => {
  const classes = useStyles();
  const { categories } = useSelector((state: RootState) => state.master);

  const {
    states: {
      dialogData,
      acordionOpen,
      answerOptionError,
      addDisabled,
      deleteDisabled,
      saveDisabled,
    },
    setters: {
      changeValue,
      changeQuestionText,
      changeAnswerOption,
      handleChangeAcordionOpen,
    },
    actions: {
      addQuestionTextOption,
      addAnswerOption,
      deleteQuestionTextOption,
      deleteAnswerOption,
      onClickSave,
      onClickDelete,
      onClickRestore,
    },
  } = props.module;

  return (
    <Dialog open={props.open} onClose={props.onClose} maxWidth="md" fullWidth>
      {dialogData.docID === undefined && (
        <DialogTitle>SHUZOロボの会話パターンを登録します</DialogTitle>
      )}
      {dialogData.docID !== undefined && (
        <DialogTitle>
          SHUZOロボの会話パターンを更新します
          {dialogData.isDeleted && "（非表示中）"}
        </DialogTitle>
      )}
      <DialogContent className={classes.dialogContent}>
        {dialogData.docID !== undefined && (
          <OutlineTextField
            label="ドキュメントID"
            value={dialogData.docID}
            disabled
          />
        )}
        <OutlineSelect
          label="カテゴリ*"
          value={dialogData.categoryId}
          options={categories}
          onChange={(e) => {
            const value = parseInt(e.target.value);
            changeValue(value, "categoryId");
          }}
          required
          isNumbering
        />
        <div className={classes.wrapper} style={{ marginBottom: 10 }}>
          <Typography className={classes.label}>ラベル*</Typography>
        </div>
        <OutlineTextField
          label="大項目*"
          value={dialogData.largeLabel ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            changeValue(value, "largeLabel");
          }}
          marginBottom={10}
        />
        <OutlineTextField
          label="中項目"
          value={dialogData.mediumLabel ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            changeValue(value, "mediumLabel");
          }}
          marginBottom={10}
        />
        <OutlineTextField
          label="小項目"
          value={dialogData.smallLabel ?? ""}
          onChange={(e) => {
            const value = e.target.value;
            changeValue(value, "smallLabel");
          }}
        />
        <div className={classes.wrapper} style={{ marginBottom: 50 }}>
          <Typography className={classes.label}>質問文*</Typography>
          <Fragment>
            <div>
              {dialogData.questionText.map((item, index) => {
                return (
                  <div
                    key={`questionText_${index}`}
                    className={classes.questionTextBlock}
                  >
                    <OutlineSelect
                      label="SHUZOの表情*"
                      value={item.face}
                      options={faceType}
                      onChange={(e) => {
                        const value = e.target.value;
                        changeQuestionText(value, index, "face");
                      }}
                      required
                      width="100%"
                      marginBottom={10}
                    />
                    <OutlineTextField
                      label="質問文"
                      value={item.text}
                      onChange={(e) => {
                        const value = e.target.value;
                        changeQuestionText(value, index, "text");
                      }}
                      width="100%"
                      marginBottom={10}
                      multiline
                      rows={3}
                    />
                    <Grid
                      container
                      className={classes.deleteButtonWrapper}
                      justify="flex-end"
                    >
                      <Button
                        onClick={() => deleteQuestionTextOption(index)}
                        variant="contained"
                        color="secondary"
                      >
                        <DeleteIcon />
                      </Button>
                    </Grid>
                  </div>
                );
              })}
            </div>
            <Grid
              container
              className={classes.addButtonWrapper}
              justify="center"
            >
              <Button
                onClick={addQuestionTextOption}
                variant="contained"
                color="secondary"
              >
                <AddIcon className={classes.addIcon} />
                追加
              </Button>
            </Grid>
          </Fragment>
        </div>
        <RadioSelect
          label="回答方式*"
          value={dialogData.textAnswer === true ? 1 : 0}
          options={[
            { label: "テキスト入力", value: 1 },
            { label: "選択式", value: 0 },
          ]}
          onChange={(e) => {
            if (e.target.value === "1") {
              changeValue(true, "textAnswer");
            } else {
              changeValue(false, "textAnswer");
            }
          }}
          marginBottom={20}
        />
        <RadioSelect
          label="複数回答*"
          value={dialogData.multiple === true ? 1 : 0}
          options={[
            { label: "可", value: 1 },
            { label: "不可", value: 0 },
          ]}
          onChange={(e) => {
            if (e.target.value === "1") {
              changeValue(true, "multiple");
            } else {
              changeValue(false, "multiple");
            }
          }}
          marginBottom={20}
        />
        <RadioSelect
          label="新規の質問かどうか*"
          value={dialogData.isNextQuestion === true ? 1 : 0}
          options={[
            { label: "新規の質問", value: 0 },
            { label: "次の質問", value: 1 },
          ]}
          onChange={(e) => {
            if (e.target.value === "1") {
              changeValue(true, "isNextQuestion");
            } else {
              changeValue(false, "isNextQuestion");
            }
          }}
          marginBottom={20}
        />

        <div className={classes.wrapper} style={{ marginBottom: 20 }}>
          <Typography className={classes.label}>
            回答*
            {answerOptionError.find((i) => i === true) && (
              <span className={classes.errorLabel}>
                ※未入力の項目があります
              </span>
            )}
          </Typography>

          {/* 回答タイプが自由回答の場合 */}
          {dialogData.textAnswer && (
            <div>
              <OutlineSelect
                label="SHUZOの表情"
                value={dialogData.answerOptions[0].face}
                options={faceType}
                onChange={(e) => {
                  const value = e.target.value;
                  changeAnswerOption(value, 0, "face");
                }}
                required
                width="100%"
                marginBottom={10}
              />
              <OutlineTextField
                label="SHUZOの反応"
                value={dialogData.answerOptions[0].reaction}
                onChange={(e) => {
                  const value = e.target.value;
                  changeAnswerOption(value, 0, "reaction");
                }}
                width="100%"
                marginBottom={10}
              />
              <OutlineTextField
                label="次の質問ID"
                value={dialogData.answerOptions[0].nextQuestion}
                onChange={(e) => {
                  const value = e.target.value;
                  changeAnswerOption(value, 0, "nextQuestion");
                }}
                width="100%"
                marginBottom={10}
              />
            </div>
          )}

          {/* 回答タイプが選択式の場合 */}
          {!dialogData.textAnswer && (
            <Fragment>
              <div>
                {dialogData.answerOptions.map((item, index) => {
                  return (
                    <Accordion
                      key={`answerOption_${index}`}
                      square
                      expanded={acordionOpen[index]}
                      onChange={(_, expanded) =>
                        handleChangeAcordionOpen(index, expanded)
                      }
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <IconButton
                          color="secondary"
                          edge="start"
                          className={classes.accordionDeleteButton}
                          onClick={(event) => {
                            event.stopPropagation();
                            deleteAnswerOption(index);
                          }}
                          onFocus={(event) => event.stopPropagation()}
                          disabled={deleteDisabled}
                        >
                          <DeleteIcon />
                        </IconButton>
                        <Typography
                          color={answerOptionError[index] ? "error" : "inherit"}
                        >
                          {`${index + 1}: ${item.text}`}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container direction="column">
                          <OutlineTextField
                            label="回答*"
                            value={item.text}
                            onChange={(e) => {
                              const value = e.target.value;
                              changeAnswerOption(value, index, "text");
                            }}
                            width="95%"
                            marginBottom={10}
                          />
                          <RadioSelect
                            label="テキスト入力*"
                            value={item.textAnswer === true ? 1 : 0}
                            options={[
                              { label: "可", value: 1 },
                              { label: "不可", value: 0 },
                            ]}
                            onChange={(e) => {
                              if (e.target.value === "1") {
                                changeAnswerOption(true, index, "textAnswer");
                              } else {
                                changeAnswerOption(false, index, "textAnswer");
                              }
                            }}
                            width="95%"
                            marginBottom={10}
                          />
                          {item.textAnswer !== true && (
                            <OutlineTextField
                              label="ノートに保存される値*"
                              value={item.value}
                              onChange={(e) => {
                                const value = e.target.value;
                                changeAnswerOption(value, index, "value");
                              }}
                              width="95%"
                              marginBottom={10}
                            />
                          )}
                          <OutlineSelect
                            label="SHUZOの表情*"
                            value={item.face}
                            options={faceType}
                            onChange={(e) => {
                              const value = e.target.value;
                              changeAnswerOption(value, index, "face");
                            }}
                            required
                            width="95%"
                            marginBottom={10}
                          />
                          <OutlineTextField
                            label="SHUZOの反応*"
                            value={item.reaction}
                            onChange={(e) => {
                              const value = e.target.value;
                              changeAnswerOption(value, index, "reaction");
                            }}
                            width="95%"
                            marginBottom={10}
                          />
                          <OutlineTextField
                            label="次の質問ID"
                            value={item.nextQuestion}
                            onChange={(e) => {
                              const value = e.target.value;
                              changeAnswerOption(value, index, "nextQuestion");
                            }}
                            width="95%"
                            marginBottom={10}
                          />
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </div>
              <Grid
                container
                className={classes.addButtonWrapper}
                justify="center"
              >
                <Button
                  onClick={addAnswerOption}
                  variant="contained"
                  color="secondary"
                  disabled={addDisabled}
                >
                  <AddIcon className={classes.addIcon} />
                  追加
                </Button>
              </Grid>
            </Fragment>
          )}
        </div>
      </DialogContent>

      <DialogActions>
        {dialogData.docID !== undefined && dialogData.isDeleted === false && (
          <div className={classes.deleteButton}>
            <Button
              onClick={onClickDelete}
              color="secondary"
              variant="contained"
            >
              <DeleteIcon />
              &nbsp;削除する
            </Button>
          </div>
        )}
        {dialogData.docID !== undefined && dialogData.isDeleted === true && (
          <div className={classes.deleteButton}>
            <Button
              onClick={onClickRestore}
              color="secondary"
              variant="contained"
            >
              <RestoreFromTrashIcon />
              &nbsp;復元する
            </Button>
          </div>
        )}
        <Button onClick={props.onClose} variant="contained">
          キャンセル
        </Button>
        <Button
          onClick={onClickSave}
          disabled={saveDisabled}
          color="secondary"
          variant="contained"
        >
          {dialogData.docID === undefined && <span>登録する</span>}
          {dialogData.docID !== undefined && <span>更新する</span>}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles({
  dialogContent: {
    paddingTop: 20,
    paddingBottom: 50,
  },
  dialogTextField: {
    width: "100%",
    marginTop: 10,
    marginBottom: 10,
  },
  errorLabel: {
    color: "#f44336",
    fontSize: 12,
    marginLeft: 10,
  },
  label: {
    marginBottom: 20,
    color: "rgba(0, 0, 0, 0.54)",
  },
  wrapper: {
    margin: "auto",
    width: "60%",
  },
  questionTextBlock: {
    marginTop: 20,
    marginBottom: 30,
  },
  accordionDeleteButton: {
    marginRight: 5,
  },
  addButtonWrapper: {
    marginTop: 20,
  },
  addIcon: {
    marginRight: 5,
  },
  deleteButtonWrapper: {
    marginTop: 10,
  },
  deleteButton: {
    flexGrow: 1,
  },
});
